// src/components/Inward.jsx

import React, { useState, useRef, useEffect } from "react";
import "../styles/layout.css";
import ReusableHeader from "./ReusableHeader";
import CustomButton from "./CustomButton";
import QRScanner from "./QRBarcodeScanner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDeviceApi } from "../api/device";
import { decodeJwt } from "../util/decodeJwt";

export default function Inward() {
  const {
    createVendorBulkDevice,
    getAllDeviceModels,
    checkVendorDeviceExists
  } = useDeviceApi();

  const [serialNumber, setSerialNumber] = useState("");
  const [addedDevices, setAddedDevices] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [userId, setUserId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [validModelIds, setValidModelIds] = useState([]);

  const fileInputRef = useRef(null);

  // ✅ Decode JWT
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setUserId(decoded?._id || "");
      setOrganizationId(decoded?.organizationId || "");
    }
  }, []);

  // ✅ Fetch models (optional future validation)
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await getAllDeviceModels({ limit: 100, offset: 0 });
        if (res.success && Array.isArray(res.data)) {
          setValidModelIds(res.data.map((m) => m.modelId));
        }
      } catch (err) {
        console.error("Failed to fetch models:", err);
      }
    };
    fetchModels();
  }, []);

  // ✅ Add Device (UI Validation)
const addDevice = (sn) => {
  const value = sn.trim().slice(0, 8); // ✅ force 8 chars

  if (!value) {
    toast.error("BCU Serial Number cannot be empty");
    return;
  }

  if (value.length < 8) {
    toast.error("BCU Serial Number must be at least 8 characters");
    return;
  }

  const exists = addedDevices.some(
    (d) => d.serialNumber.toLowerCase() === value.toLowerCase()
  );

  if (exists) {
    toast.error("This BCU Serial Number is already added");
    return;
  }

  setAddedDevices((prev) => [...prev, { serialNumber: value }]);
};

  // ✅ Manual Add
const handleAddClick = async () => {
  if (!serialNumber) {
    toast.error("BCU Serial Number is required");
    return;
  }

  if (serialNumber.length !== 8) {
    toast.error("BCU Serial Number must be exactly 8 characters");
    return;
  }

  try {
    const res = await checkVendorDeviceExists({ serialNumber });

    if (res.exists) {
      toast.error("Device already exists in system");
      return;
    }

    addDevice(serialNumber);
    setSerialNumber("");

  } catch (err) {
    toast.error(err.message || "Validation failed");
  }
};

  // ✅ Remove
  const handleRemoveClick = (id) => {
    setAddedDevices((prev) =>
      prev.filter((d) => d.serialNumber !== id)
    );
    toast.info(`Removed: ${id}`);
  };

  // ✅ Camera Scan (QR + Barcode)
const handleScanDetected = async (data) => {
  try {
    const text = String(data).trim();

    if (!text) {
      toast.error("Invalid QR/Barcode");
      return;
    }

    // ✅ always take first 8 characters
    const trimmedValue = text.slice(0, 8);

    if (text.length > 8) {
      toast.info("Serial trimmed to first 8 characters");
    }

    setSerialNumber(trimmedValue);

  } catch (err) {
    toast.error("Failed to read code");
  }

  setShowScanner(false);
};

  // ✅ Upload Image Scan (QR + Barcode)
const handleFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    const reader = new BrowserMultiFormatReader();

    const img = document.createElement("img");
    img.src = URL.createObjectURL(file);

    await new Promise((resolve) => (img.onload = resolve));

    const result = await reader.decodeFromImageElement(img);

    const text = result?.text?.trim();

    if (!text) {
      toast.error("Invalid QR/Barcode image");
      return;
    }

    // ✅ always take first 8 characters
    const trimmedValue = text.slice(0, 8);

    if (text.length > 8) {
      toast.info("Serial trimmed to first 8 characters");
    }

    setSerialNumber(trimmedValue);

  } catch (err) {
    toast.error("Unreadable QR/Barcode image");
  } finally {
    e.target.value = "";
  }
};

  // ✅ Submit Inward
  const handleInwardSubmit = async () => {
    if (addedDevices.length === 0) {
      toast.error("No devices to inward");
      return;
    }

    const payload = {
      status: "available",
      createdBy: userId,
      organizationId,
      devices: addedDevices
    };

    try {
      const res = await createVendorBulkDevice(payload);

      toast.success(
        `Inward completed: ${res.successIds?.length || 0} success, ${res.failedIds?.length || 0} failed`
      );

      setAddedDevices([]);

    } catch (err) {
      toast.error(err.message || "Inward failed");
    }
  };

  const totalCount = addedDevices.length;

  return (
    <div className="inventoryManagement">
      <ReusableHeader title="Device Inward" />

      <div className="inward-container">
        {/* LEFT SIDE */}
        <div className="left-side">

          {/* Manual Entry */}
          <div className="box">
            <div className="input-block">
              <input
                type="text"
                placeholder="Enter BCU Serial Number"
                value={serialNumber}
                maxLength={8}
               onChange={(e) =>
                    setSerialNumber(e.target.value.slice(0, 8))
                  }
                className="input-manufacturer"
              />
            </div>

            <CustomButton
              type="button"
              className="btn-add"
              onClick={handleAddClick}
            >
              Add
            </CustomButton>
          </div>

          <div className="or-text">OR</div>

          {/* QR / Barcode Section */}
          <div className="box">
            <div className="box-label">Scan QR / Barcode</div>

            <div className="qr-box">
              {showScanner && (
                <QRScanner
                  onDetected={handleScanDetected}
                  onClose={() => setShowScanner(false)}
                />
              )}
            </div>

            <div className="button-group">
              <CustomButton
                type="button"
                className="btn-scan"
                onClick={() => setShowScanner((prev) => !prev)}
              >
                {showScanner ? "Close Scanner" : "Scan Code"}
              </CustomButton>

              <CustomButton
                type="button"
                className="btn-scan"
                onClick={() => fileInputRef.current.click()}
              >
                Upload Image
              </CustomButton>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="right-side">
          <div className="added-devices-label">
            Added Devices
          </div>

          <div className="added-devices-container-inward">
            {addedDevices.length === 0 ? (
              <p className="no-device-text">
                No devices added yet
              </p>
            ) : (
              <div className="device-list">
                {addedDevices.map((d, index) => (
                  <div key={index} className="device-card">
                    <p>
                      BCU Serial Number:{" "}
                      <span>{d.serialNumber}</span>
                    </p>
                    <button
                      className="remove-btn"
                      onClick={() =>
                        handleRemoveClick(d.serialNumber)
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <CustomButton
            type="button"
            className="btn-inward"
            onClick={handleInwardSubmit}
          >
            Inward ({totalCount})
          </CustomButton>
        </div>
      </div>
    </div>
  );
}